/**
 * First-touch attribution capture.
 *
 * On the visitor's first page load (before signup), we snapshot:
 *  - UTM parameters (utm_source/medium/campaign/content/term)
 *  - Click IDs (gclid, fbclid)
 *  - Meta browser cookies (_fbp, _fbc) — read on demand at signup time
 *  - document.referrer
 *  - landing path
 *
 * Stored in localStorage under a single key. The snapshot is *first-touch*:
 * once written it is never overwritten (so re-visits don't clobber the
 * original campaign that brought the user in).
 *
 * At signup, `getSignupAttributionMetadata()` builds the metadata object
 * that gets passed to supabase.auth.signUp via `options.data`, where the
 * `handle_new_user` trigger persists it into the profiles table.
 */
const STORAGE_KEY = "nimi_first_touch_attribution_v1";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

export type FirstTouchAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landing_path?: string;
  captured_at?: string;
};

const isBrowser = () => typeof window !== "undefined";

const safeReadStorage = (): FirstTouchAttribution | null => {
  if (!isBrowser()) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FirstTouchAttribution) : null;
  } catch {
    return null;
  }
};

const safeWriteStorage = (value: FirstTouchAttribution) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore (private mode, quota, etc.)
  }
};

const readCookie = (name: string): string | undefined => {
  if (!isBrowser()) return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]+)")
  );
  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * Capture first-touch attribution from the current URL and document.
 * No-op if a snapshot already exists (first-touch is sticky).
 */
export const captureFirstTouchAttribution = (): FirstTouchAttribution | null => {
  if (!isBrowser()) return null;

  const existing = safeReadStorage();
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const snapshot: FirstTouchAttribution = {};

  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v) snapshot[key] = v;
  }

  const gclid = params.get("gclid");
  if (gclid) snapshot.gclid = gclid;

  const fbclid = params.get("fbclid");
  if (fbclid) snapshot.fbclid = fbclid;

  // Same-origin referrer is noise; skip it.
  const ref = document.referrer;
  if (ref && !ref.startsWith(window.location.origin)) {
    snapshot.referrer = ref;
  }

  snapshot.landing_path = window.location.pathname + window.location.search;
  snapshot.captured_at = new Date().toISOString();

  // Only persist if we actually captured something meaningful (beyond just landing_path).
  const hasSignal =
    UTM_KEYS.some((k) => snapshot[k]) ||
    snapshot.gclid ||
    snapshot.fbclid ||
    snapshot.referrer;

  if (hasSignal) {
    safeWriteStorage(snapshot);
    return snapshot;
  }

  // Still record landing path + timestamp so we know we've initialized,
  // but without overwriting a future real first-touch hit on this device.
  return null;
};

export const getFirstTouchAttribution = (): FirstTouchAttribution | null =>
  safeReadStorage();

/**
 * Build the metadata object passed to supabase.auth.signUp's options.data.
 * Maps localStorage attribution + live cookies to the `signup_*` columns
 * that `handle_new_user` reads from raw_user_meta_data.
 *
 * `heardAboutSource` comes from the signup form question and is included
 * here so it's atomic with the auth.signUp call.
 */
export const getSignupAttributionMetadata = (
  heardAboutSource?: string
): Record<string, string> => {
  const meta: Record<string, string> = {};
  const attr = getFirstTouchAttribution();

  if (attr) {
    if (attr.utm_source) meta.signup_utm_source = attr.utm_source;
    if (attr.utm_medium) meta.signup_utm_medium = attr.utm_medium;
    if (attr.utm_campaign) meta.signup_utm_campaign = attr.utm_campaign;
    if (attr.utm_content) meta.signup_utm_content = attr.utm_content;
    if (attr.utm_term) meta.signup_utm_term = attr.utm_term;
    if (attr.gclid) meta.signup_gclid = attr.gclid;
    if (attr.fbclid) meta.signup_fbclid = attr.fbclid;
    if (attr.referrer) meta.signup_referrer = attr.referrer;
    if (attr.landing_path) meta.signup_landing_path = attr.landing_path;
  }

  // Read Meta Pixel cookies live at signup time (more accurate than first-touch).
  const fbp = readCookie("_fbp");
  if (fbp) meta.signup_fbp = fbp;

  const fbc = readCookie("_fbc");
  if (fbc) meta.signup_fbc = fbc;

  // Capture UA at signup for Meta CAPI match-quality (no IP — needs Auth Hook).
  if (isBrowser() && navigator.userAgent) {
    meta.signup_user_agent = navigator.userAgent;
  }

  if (heardAboutSource && heardAboutSource.trim()) {
    meta.heard_about_source = heardAboutSource.trim();
  }

  return meta;
};
